import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Phone, AlertTriangle, XCircle } from 'lucide-react';

type ChannelType = 'whatsapp' | 'email' | 'sms' | 'protesto' | 'negativacao';

export interface CollectionStep {
  id: string;
  step_number: number;
  trigger_days: number;
  channel: ChannelType;
  is_active: boolean | null;
  template_id?: string | null;
  collection_rule_id?: string;
  company_id?: string;
  timing_mode?: 'relative' | 'in_order';
}

interface ReguaTimelineProps {
  ruleId: string;
  onAddStep?: () => void; // kept for interface compatibility
  onEditStep?: (step: CollectionStep) => void;
}

// WhatsApp icon component - full logo for better visibility
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 3.67C14.25 3.67 16.31 4.53 17.87 6.09C19.42 7.65 20.28 9.72 20.28 11.92C20.28 16.46 16.58 20.15 12.04 20.15C10.56 20.15 9.11 19.76 7.85 19L7.55 18.83L4.43 19.65L5.26 16.61L5.06 16.29C4.24 14.98 3.8 13.46 3.8 11.91C3.81 7.37 7.5 3.67 12.05 3.67ZM8.53 7.33C8.37 7.33 8.1 7.39 7.87 7.64C7.65 7.89 7 8.5 7 9.71C7 10.93 7.89 12.1 8 12.27C8.14 12.44 9.76 14.94 12.25 16C12.84 16.27 13.3 16.42 13.66 16.53C14.25 16.72 14.79 16.69 15.22 16.63C15.7 16.56 16.68 16.03 16.89 15.45C17.1 14.87 17.1 14.38 17.04 14.27C16.97 14.17 16.81 14.11 16.56 14C16.31 13.86 15.09 13.26 14.87 13.18C14.64 13.1 14.5 13.06 14.31 13.3C14.15 13.55 13.67 14.11 13.53 14.27C13.38 14.44 13.24 14.46 13 14.34C12.74 14.21 11.94 13.95 11 13.11C10.26 12.45 9.77 11.64 9.62 11.39C9.5 11.15 9.61 11 9.73 10.89C9.84 10.78 10 10.6 10.1 10.45C10.23 10.31 10.27 10.2 10.35 10.04C10.43 9.87 10.39 9.73 10.33 9.61C10.27 9.5 9.77 8.26 9.56 7.77C9.36 7.29 9.16 7.35 9 7.34C8.86 7.34 8.7 7.33 8.53 7.33Z"/>
  </svg>
);

const channelIcons: Record<ChannelType, React.FC<{ className?: string }>> = {
  whatsapp: WhatsAppIcon,
  email: Mail,
  sms: Phone,
  protesto: AlertTriangle,
  negativacao: XCircle
};

// Get color based on trigger days:
// < 0: Blue (before due), = 0: Green (on due), > 0 and < 20: Yellow, >= 20: Red
const getTimelineColor = (triggerDays: number): string => {
  if (triggerDays < 0) return 'text-[hsl(var(--basic-600))]'; // Before due: blue
  if (triggerDays === 0) return 'text-[hsl(var(--accent-600))]'; // On due: green
  if (triggerDays < 20) return 'text-[hsl(var(--careful-600))]'; // 1-19 days late: yellow
  return 'text-destructive'; // 20+ days late: red
};

const getDotColor = (triggerDays: number): string => {
  if (triggerDays < 0) return 'bg-[hsl(var(--basic-500))]'; // Blue
  if (triggerDays === 0) return 'bg-[hsl(var(--accent-500))]'; // Green
  if (triggerDays < 20) return 'bg-[hsl(var(--careful-500))]'; // Yellow
  return 'bg-destructive'; // Red
};

// Line colors are now simplified: blue (before) and red (after)

export function ReguaTimeline({ ruleId, onEditStep }: ReguaTimelineProps) {
  const { data: steps = [], isLoading } = useQuery({
    queryKey: ['collection-steps', ruleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('collection_steps')
        .select('*')
        .eq('collection_rule_id', ruleId)
        .order('trigger_days');

      if (error) throw error;
      return data as CollectionStep[];
    }
  });

  if (isLoading) {
    return <div className="text-center text-muted-foreground text-sm py-4">Carregando...</div>;
  }

  if (steps.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        Clique em <b>+</b> para adicionar etapas de comunicação
      </div>
    );
  }

  // Group steps by before/on/after due date
  const beforeDue = steps.filter(s => s.trigger_days < 0).sort((a, b) => a.trigger_days - b.trigger_days);
  const onDue = steps.filter(s => s.trigger_days === 0);
  const afterDue = steps.filter(s => s.trigger_days > 0).sort((a, b) => a.trigger_days - b.trigger_days);

  return (
    <div className="w-full max-w-3xl">
      {/* Labels - always show all three, centered */}
      <div className="grid grid-cols-3 mb-2 px-4">
        <span className="text-xs text-muted-foreground text-left">
          Antes do<br />vencimento
        </span>
        <span className="text-xs text-muted-foreground text-center">
          No dia do<br />vencimento
        </span>
        <span className="text-xs text-muted-foreground text-right">
          Depois do<br />vencimento
        </span>
      </div>

      {/* Timeline visualization - continuous line with centered dots */}
      <div className="relative min-h-[60px]">
        {/* Continuous timeline line - positioned at dot center */}
        {/* Number h-6 (24px) + spacer h-1 (4px) + margin my-1 (4px top) + half dot (4px) - half line (2px) = 34px */}
        <div className="absolute left-0 right-0 top-[34px] h-1 flex">
          {/* Blue line (left half) */}
          <div className="flex-1 h-1 bg-[hsl(var(--basic-500))] rounded-l-full" />
          {/* Red line (right half) */}
          <div className="flex-1 h-1 bg-destructive rounded-r-full" />
        </div>

        {/* Steps container - 3 column grid */}
        <div className="relative grid grid-cols-[1fr_auto_1fr] items-start z-10">
          {/* Before section (left) */}
          <div className="flex items-start justify-end gap-6 pr-4">
            {beforeDue.map((step) => {
              const Icon = channelIcons[step.channel];
              const iconColor = getTimelineColor(step.trigger_days);
              const dotColor = getDotColor(step.trigger_days);
              return (
                <div 
                  key={step.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditStep?.(step);
                  }}
                  className="flex flex-col items-center cursor-pointer group"
                >
                  <span className="text-sm font-bold text-foreground h-6 flex items-end justify-center">
                    {Math.abs(step.trigger_days).toString().padStart(2, '0')}
                  </span>
                  <div className="h-1 mt-1" /> {/* Spacer: 4px height + 4px top margin = 8px */}
                  <div className={`w-3 h-3 rounded-full ${dotColor}`} />
                  <div className="h-1 mt-1" /> {/* Spacer after dot */}
                  <Icon className={`h-6 w-6 ${iconColor} group-hover:scale-110 transition-transform`} />
                </div>
              );
            })}
          </div>

          {/* Center - Due date marker (always centered) */}
          <div className="flex flex-col items-center px-2">
            <span className="text-sm font-bold text-foreground h-6 flex items-end justify-center">0</span>
            <div className="h-1 mt-1" /> {/* Spacer: 4px height + 4px top margin = 8px */}
            <div className="w-3 h-3 rounded-full bg-[hsl(var(--accent-500))]" />
            <div className="h-1 mt-0.5" /> {/* Spacer after dot */}
            {/* On due icons */}
            <div className="flex gap-1">
              {onDue.map((step) => {
                const Icon = channelIcons[step.channel];
                return (
                  <div
                    key={step.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditStep?.(step);
                    }}
                    className="cursor-pointer group"
                  >
                    <Icon className={`h-6 w-6 text-[hsl(var(--accent-600))] group-hover:scale-110 transition-transform`} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* After section (right) */}
          <div className="flex items-start justify-start gap-6 pl-4">
            {afterDue.map((step) => {
              const Icon = channelIcons[step.channel];
              const iconColor = getTimelineColor(step.trigger_days);
              const dotColor = getDotColor(step.trigger_days);
              return (
                <div 
                  key={step.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditStep?.(step);
                  }}
                  className="flex flex-col items-center cursor-pointer group"
                >
                  <span className="text-sm font-bold text-foreground h-6 flex items-end justify-center">
                    {step.trigger_days.toString().padStart(2, '0')}
                  </span>
                  <div className="h-1 mt-1" /> {/* Spacer: 4px height + 4px top margin = 8px */}
                  <div className={`w-3 h-3 rounded-full ${dotColor}`} />
                  <div className="h-1 mt-1" /> {/* Spacer after dot */}
                  <Icon className={`h-6 w-6 ${iconColor} group-hover:scale-110 transition-transform`} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
